/* ── Sniper Reticle Laser ─────────────────────────────────────────
 * Bright additive laser segment from caster to target. ~0.4s lifetime.
 *
 * Implementation: a unit-length CylinderGeometry (radius 0.08), oriented
 * via lookAt and scaled to span the caster→target distance. Cylinder
 * geometry has its axis along Y, so we lookAt(target) and rotate -90°
 * around X is unnecessary — we use the standard "align to vector" trick.
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec3, vec4, float, smoothstep, abs } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 0.4;
const LASER_RADIUS = 0.18;
const COLOR_HEX = 0xfff2a0;

// Module-scope scratch vectors so applyTransform doesn't allocate per tick.
const _up = new THREE.Vector3(0, 1, 0);
const _xAxis = new THREE.Vector3(1, 0, 0);
const _dir = new THREE.Vector3();
const _mid = new THREE.Vector3();

export class SniperReticleLaser extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.CylinderGeometry;
  private readonly material: THREE.MeshBasicNodeMaterial;
  private readonly uProgress: ReturnType<typeof uniform>;
  private readonly fromV = new THREE.Vector3();
  private readonly toV = new THREE.Vector3();
  private readonly fromResolver?: LivePositionResolver;
  private readonly toResolver?: LivePositionResolver;

  constructor(
    scene: THREE.Scene,
    from: Vec3Like,
    to: Vec3Like,
    options?: { fromResolver?: LivePositionResolver; toResolver?: LivePositionResolver },
  ) {
    super(scene, LIFE_SECONDS);
    this.uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const uProgress = this.uProgress;

    // Unit-height cylinder (radius LASER_RADIUS), centered at origin, axis +Y.
    // We scale Y to laser length and orient via lookAt.
    this.geometry = new THREE.CylinderGeometry(LASER_RADIUS, LASER_RADIUS, 1, 8, 1, true);
    this.material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    const colorNode = Fn(() => {
      const u = uv();
      // Bright core with fade toward edges (uv.x runs across the cylinder).
      const core = float(1.0).sub(abs(u.x.sub(0.5)).mul(2.0));
      const fade = float(uProgress).oneMinus();
      const rgb = vec3(uColor).add(vec3(1.0, 1.0, 1.0).mul(core.mul(fade).mul(0.5)));
      const alpha = smoothstep(float(0.0), float(1.0), core).mul(fade);
      return vec4(rgb, alpha);
    })();
    this.material.colorNode = colorNode;
    setEmissiveNode(this.material, colorNode);

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.fromResolver = options?.fromResolver;
    this.toResolver = options?.toResolver;

    this.fromV.set(from.x, from.y, from.z);
    this.toV.set(to.x, to.y, to.z);
    this.applyTransform();

    this.scene.add(this.mesh);
  }

  /** Recompute mid-point, scale, and orientation from current fromV/toV. */
  private applyTransform(): void {
    _dir.subVectors(this.toV, this.fromV);
    const length = _dir.length();
    _mid.addVectors(this.fromV, this.toV).multiplyScalar(0.5);
    this.mesh.position.copy(_mid);
    this.mesh.scale.set(1, Math.max(length, 0.001), 1);

    if (length > 1e-6) {
      // Cylinder default axis is +Y. Align +Y to dir.
      _dir.normalize();
      const dot = _up.dot(_dir);
      // setFromUnitVectors degenerates when up and dir are antiparallel
      // (dot ≈ -1): the implementation picks an arbitrary perpendicular
      // axis for the 180° rotation, which jitters across calls. Pin to X.
      if (Math.abs(dot + 1) < 1e-3) {
        this.mesh.quaternion.setFromAxisAngle(_xAxis, Math.PI);
      } else {
        this.mesh.quaternion.setFromUnitVectors(_up, _dir);
      }
    }
  }

  protected tick(t: number): void {
    this.uProgress.value = t;
    let moved = false;
    if (this.fromResolver) {
      const live = this.fromResolver();
      if (live) {
        this.fromV.set(live.x, live.y, live.z);
        moved = true;
      }
    }
    if (this.toResolver) {
      const live = this.toResolver();
      if (live) {
        this.toV.set(live.x, live.y, live.z);
        moved = true;
      }
    }
    if (moved) this.applyTransform();
  }

  protected disposeResources(): void {
    this.scene.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }
}
